import { useEffect, useState } from 'react';
import {
    analyticsApi,
    buildTableQuery,
    type ActivationFunnel as ActivationFunnelData,
    type AnalyticsFilters,
    type AnalyticsTable,
    type FunnelStageId,
} from '../../services/analytics';
import { FUNNEL_COLUMNS, FUNNEL_FORMATTERS } from './accountTables';
import { DataTable } from './DataTable';
import { FunnelChart } from './FunnelChart';
import { FUNNEL_STAGE_LABELS, observationNote } from './funnel';
import { formatInt } from './format';
import { InfoTip } from './InfoTip';
import { METRIC_INFO } from './metricInfo';
import styles from './ActivationFunnel.module.css';

interface ActivationFunnelProps {
    funil: ActivationFunnelData;
    filters: AnalyticsFilters;
    /** Falso enquanto o periodo personalizado esta incompleto: nada e consultado. */
    enabled: boolean;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
    timeZone: string;
}

interface Settled {
    key: string;
    table: AnalyticsTable | null;
    error: string | null;
}

export function ActivationFunnel({ funil, filters, enabled, refreshKey, timeZone }: ActivationFunnelProps) {
    const [stage, setStage] = useState<FunnelStageId>('cadastrou');
    const [retry, setRetry] = useState(0);
    const [settled, setSettled] = useState<Settled | null>(null);

    const requestKey = enabled ? `${buildTableQuery(filters, { stage })}#${refreshKey}.${retry}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .table('funil', filters, { stage }, controller.signal)
            .then((table) => setSettled({ key: requestKey, table, error: null }))
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    table: null,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar as contas.',
                });
            });
        return () => controller.abort();
        // requestKey resume filtros, etapa e contadores de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const current = settled && settled.key === requestKey ? settled : null;
    // Mantem a ultima tabela na tela enquanto a nova carrega, para nao piscar.
    const table = current ? current.table : (settled?.table ?? null);

    return (
        <section className={styles.card} aria-label="Funil de ativação">
            <header className={styles.header}>
                <h4 className={styles.title}>
                    Funil de ativação
                    <InfoTip info={METRIC_INFO.funilAtivacao} label="Funil de ativação" align="start" />
                </h4>
                <span className={styles.note}>{observationNote(funil)}</span>
            </header>

            <FunnelChart funil={funil} />

            <div className={styles.stages} role="group" aria-label="Etapa do funil">
                {funil.stages.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`${styles.chip} ${s.id === stage ? styles.chipActive : ''}`}
                        aria-pressed={s.id === stage}
                        onClick={() => setStage(s.id)}
                    >
                        {FUNNEL_STAGE_LABELS[s.id]} <strong>{formatInt(s.count)}</strong>
                    </button>
                ))}
            </div>

            <DataTable
                key={stage}
                title={
                    <>
                        Contas na etapa: {FUNNEL_STAGE_LABELS[stage]}
                        <InfoTip info={METRIC_INFO.contasNaEtapa} label="Contas na etapa" align="start" />
                    </>
                }
                hint="Selecione outra etapa para estratificar"
                table={table}
                loading={loading}
                error={current?.error ?? null}
                onRetry={() => setRetry((n) => n + 1)}
                timeZone={timeZone}
                columns={FUNNEL_COLUMNS}
                formatters={FUNNEL_FORMATTERS}
                emptyMessage="Nenhuma conta nesta etapa."
                exportFilename={`analytics-funil-${stage}.xlsx`}
            />
        </section>
    );
}
